/**
 * Column — point-placed structure (STRUCTURE round, 2026-08 — docs/bim-plan/08 Part 2).
 * DESIGNER-PLACED only: no auto-derived framing, no footings (Addendum 09 §5 keeps
 * structural/topography-dependent derivation out of scope on purpose).
 *
 * Position = a single-node `path` param at the column's CENTER (the object-category
 * anchor trick, minus the back-center offset — a column has no "front"): the shared
 * machinery works unchanged — translateElement probes `path`, duplicateElement
 * refreshes its node id, the pet-palette Move arms the standard two-click translate,
 * and kind 'path2d' keeps it out of eyedropper transfer automatically.
 *
 * VERTICAL EXTENT — the levels-as-datums contract: base = element level + baseOffsetFt;
 * top = either an unconnected height (the wall model) or a LEVEL reference + offset
 * (`topLevelId`, the first use of param kind 'level' — resolveElement materializes the
 * record into `el.paramLevels.topLevelId` so generators never re-look levels up). The
 * instance param names deliberately reuse the wall vocabulary (`baseOffsetFt`,
 * `unconnectedHeightFt`) so HandleLayer's vtop/vbase section grips work verbatim.
 *
 * PROFILE is a TYPE choice (firm standards): rectangular (width × depth) or round
 * (diameter). Rotation only means anything on a rectangle — `conditionalOn` hides it
 * for round profiles, and placement skips the aim stage entirely.
 */
import type { CategorySchema } from '../../registry'

export const columnSchema: CategorySchema = {
  id: 'column',
  pickRank: 1,
  label: 'Column',
  typeParams: {
    groups: [
      {
        label: 'Profile',
        params: [
          {
            key: 'profile', label: 'Profile', kind: 'choice', default: 'rect',
            choices: [
              { value: 'rect', label: 'Rectangular' },
              { value: 'round', label: 'Round' },
            ],
          },
          {
            key: 'widthFt', label: 'Width', kind: 'length', default: 3.5 / 12, min: 0.05, max: 6,
            conditionalOn: { param: 'profile', equals: 'rect' },
          },
          {
            key: 'depthFt', label: 'Depth', kind: 'length', default: 3.5 / 12, min: 0.05, max: 6,
            conditionalOn: { param: 'profile', equals: 'rect' },
          },
          {
            key: 'diameterFt', label: 'Diameter', kind: 'length', default: 4.5 / 12, min: 0.05, max: 6,
            conditionalOn: { param: 'profile', equals: 'round' },
          },
        ],
      },
      {
        label: 'Appearance',
        params: [
          // the whole member IS one material (a steel/wood/concrete column is a
          // material choice, not a layer stack) — drives 3D color AND cut poché
          { key: 'material', label: 'Material', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // COLORS (2026-10-05): the color brushed on the whole member with the Paint Brush (a
        // colors.ts id) — 3D only; the TYPE material above is what the member IS (poché, caps)
        label: 'Finish',
        params: [
          { key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        label: 'Placement',
        params: [
          // single-node anchor path at the column CENTER — see the header note
          { key: 'path', label: 'Anchor', kind: 'path2d', default: null },
          {
            // 0 = width along world x; meaningless for round profiles (hidden).
            // Positional: copying it onto another column would spin it, not restyle it.
            key: 'rotationDeg', label: 'Rotation', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
            conditionalOn: { param: 'profile', equals: 'rect' },
          },
        ],
      },
      {
        label: 'Constraints',
        params: [
          { key: 'baseOffsetFt', label: 'Base Offset', kind: 'length', default: 0, min: -20, max: 20 },
          {
            key: 'topMode', label: 'Top', kind: 'choice', default: 'height',
            choices: [
              { value: 'height', label: 'Unconnected height' },
              { value: 'level', label: 'Up to level' },
            ],
          },
          {
            key: 'unconnectedHeightFt', label: 'Unconnected Height', kind: 'length', default: 8, min: 0.5, max: 60,
            conditionalOn: { param: 'topMode', equals: 'height' },
          },
          {
            // '' = no level chosen yet — the generator falls back to the unconnected
            // height so a half-configured column never vanishes
            key: 'topLevelId', label: 'Top Level', kind: 'level', default: '',
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
          {
            key: 'topOffsetFt', label: 'Top Offset', kind: 'length', default: 0, min: -20, max: 20,
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
        ],
      },
    ],
  },
}
